import { Check, Minus } from 'lucide-react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/** La casella vuota, uguale anche nelle voci a spunta dei menu. */
export const checkboxBoxClass =
  'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-strong bg-default text-on-emphasis transition-colors';

const LABEL_SIZE = { md: 'text-sm', sm: 'text-caption' };

type CheckboxProps = ComponentProps<typeof CheckboxPrimitive.Root> & {
  size?: keyof typeof LABEL_SIZE;
};

/**
 * Una casella con la sua etichetta: `checked` / `onCheckedChange`, come in shadcn.
 * `checked="indeterminate"` mostra il trattino di una selezione parziale; senza
 * etichetta visibile la casella vuole un `aria-label`. `size="sm"` per le toolbar.
 */
export function Checkbox({ className, children, checked, size = 'md', ...props }: CheckboxProps) {
  return (
    <label
      className={cn(
        'flex items-start gap-2 text-default',
        LABEL_SIZE[size],
        'has-disabled:cursor-not-allowed has-disabled:opacity-60',
        className
      )}
    >
      {/* Alta una riga di testo: la casella resta centrata sulla prima riga dell'etichetta. */}
      <span className="flex h-lh shrink-0 items-center">
        <CheckboxPrimitive.Root
          checked={checked}
          className={cn(
            checkboxBoxClass,
            'data-[state=checked]:border-transparent data-[state=checked]:bg-primary',
            'data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-primary',
            'aria-invalid:border-danger-emphasis focus-visible:focus-ring'
          )}
          {...props}
        >
          <CheckboxPrimitive.Indicator>
            {checked === 'indeterminate' ? (
              <Minus aria-hidden className="h-3 w-3" />
            ) : (
              <Check aria-hidden className="h-3 w-3" />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      </span>
      {children}
    </label>
  );
}
